import { httpClient } from './http';
import { API_ENDPOINTS } from '../config/api';

export interface StripeFeatureItem {
  description: string;
  checked?: boolean;
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  images: string[];
  metadata: Record<string, string>;
  prices: StripePrice[];
  features?: StripeFeatureItem[];
}

export interface StripePrice {
  id: string;
  currency: string;
  unit_amount: number;
  recurring?: {
    interval: 'month' | 'year';
    interval_count: number;
  };
  type: 'one_time' | 'recurring';
}

export const stripeProductsService = {
  /**
   * Obtiene todos los productos de Stripe
   */
  async getProducts(): Promise<StripeProduct[]> {
    try {
      const response = await httpClient.get<StripeProduct[]>(
        API_ENDPOINTS.STRIPE_PRODUCTS,
        undefined,
        'public'
      );
      
      // El backend ahora devuelve el array directo
      return response || [];
    } catch (error) {
      console.error('Error obteniendo productos de Stripe:', error);
      throw error;
    }
  },

  /**
   * Obtiene un producto específico de Stripe
   */
  async getProduct(productId: string): Promise<StripeProduct> {
    try {
      const response = await httpClient.get<StripeProduct>(
        API_ENDPOINTS.STRIPE_PRODUCT_BY_ID(productId),
        undefined,
        'public'
      );
      
      return response;
    } catch (error) {
      console.error('Error obteniendo producto de Stripe:', error);
      throw error;
    }
  },

  /**
   * Obtiene los precios de un producto específico
   */
  async getProductPrices(productId: string): Promise<StripePrice[]> {
    try {
      const response = await httpClient.get<StripePrice[]>(
        API_ENDPOINTS.STRIPE_PRODUCT_PRICES(productId),
        undefined,
        'public'
      );
      
      return response || [];
    } catch (error) {
      console.error('Error obteniendo precios de Stripe:', error);
      throw error;
    }
  },

  /**
   * Formatea el precio para mostrar
   */
  formatPrice(price: StripePrice): string {
    const amount = price.unit_amount / 100; // Stripe usa centavos
    const currency = price.currency.toUpperCase();
    
    if (price.recurring) {
      const interval = price.recurring.interval === 'month' ? 'mes' : 'año';
      return `$${amount} ${currency}/${interval}`;
    }
    
    return `$${amount} ${currency}`;
  },

  /**
   * Obtiene productos filtrados por tipo (suscripción, etc.)
   */
  async getProductsByType(type: string): Promise<StripeProduct[]> {
    const products = await this.getProducts();
    return products.filter(product => 
      product.metadata.type === type
    );
  }
}; 